import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

import { deleteDriver, getDrivers } from "../../api/driver";
import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../api/DeliveryCenter/deliveryCenter";
import RemoveModal from "../modals/removeModal";
import DriverListRow from "../drivers/DriverRow";
import DriversCreateEdit from "../drivers/driversCreateEdit";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../modals/AnimatedModal";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";

// Icons
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SmsIcon from "@mui/icons-material/Sms";

import ConfigSwitchCard from "../styled/ConfigSwitchCard";

// Utils
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import Paper from "@mui/material/Paper";

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function DeliveryTeamModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [checked, setChecked] = useState(false);
  const [checkedSms, setCheckedSms] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driverToEditId, setDriverToEditId] = useState(null);
  const [showCreateEditDriverModal, setShowCreateEditDriverModal] =
    useState(false);
  const [driverToRemoveId, setDriverToRemoveId] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [page, setPage] = useState(1);
  const [showButtonMore, setShowButtonMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // For forcing re-render after loading
  const [forceUpdate, setForceUpdate] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setLoading(true);
    Promise.all([loadDrivers(true, null), loadConfig()])
      .then(() => {
        setIsFirstLoad(false);
        // Force update to ensure animations work correctly
        setForceUpdate((prev) => prev + 1);
      })
      .catch((error) => {
        setErrorMessage(
          "Ocorreu um erro ao carregar os dados. Tente novamente."
        );
        setShowError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [show]);

  // Filter drivers based on search term
  const filteredDrivers = useMemo(() => {
    if (!searchTerm) return drivers;

    return drivers.filter((driver) => {
      const driverName = driver.name?.toLowerCase() || "";
      const driverPhone = driver.phone?.toLowerCase() || "";
      const driverPlate = driver.plate?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();

      return (
        driverName.includes(searchLower) ||
        driverPhone.includes(searchLower) ||
        driverPlate.includes(searchLower)
      );
    });
  }, [drivers, searchTerm]);

  const loadDrivers = async (newSearch, filtersData) => {
    try {
      if (newSearch) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const { data } = await getDrivers();
      data.sort((a, b) => a.name.localeCompare(b.name));

      let result;
      if (newSearch) {
        result = [...data];
        switchShowButtonMore(data.length);
      } else {
        const new_drivers = data.filter(
          ({ driver_id: id1 }) =>
            !drivers.some(({ driver_id: id2 }) => id2 === id1)
        );
        result = [...drivers, ...new_drivers];
        switchShowButtonMore(new_drivers.length);
      }

      setPage(page + 1);
      setDrivers(result);

      // Force update to ensure animations work correctly
      setTimeout(() => {
        setForceUpdate((prev) => prev + 1);
      }, 100);
    } catch (err) {
      console.log(err);
      setErrorMessage("Erro ao carregar entregadores. Tente novamente.");
      setShowError(true);
      dispatchSnackBarMessage(
        "drivers-load-error",
        "Erro ao carregar entregadores",
        "error"
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadConfig = async () => {
    try {
      const params = {
        config: "block_delivering_drivers,send_driver_sms_maps_link",
      };
      const {
        data: { block_delivering_drivers, send_driver_sms_maps_link },
      } = await getDeliveryCenterConfig(params);
      setChecked(block_delivering_drivers);
      setCheckedSms(send_driver_sms_maps_link);
    } catch (err) {
      console.log(err);
      dispatchSnackBarMessage(
        "config-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    }
  };

  const switchShowButtonMore = (length) => {
    length > 0 ? setShowButtonMore(true) : setShowButtonMore(false);
  };

  const openCreateDriverModal = () => {
    setDriverToEditId(null);
    setShowCreateEditDriverModal(true);
  };

  const closeCreateEditDriverModal = useCallback(() => {
    setShowCreateEditDriverModal(false);
  }, []);

  const editDriver = useCallback((id) => {
    setDriverToEditId(id);
    setShowCreateEditDriverModal(true);
  }, []);

  const toggleDeleteModal = useCallback(
    (id) => {
      setDriverToRemoveId(id);
      setShowRemoveModal(!showRemoveModal);
    },
    [showRemoveModal]
  );

  const handleDeleteDriver = async () => {
    try {
      setLoading(true);
      await deleteDriver(driverToRemoveId);
      await loadDrivers(true);
      setShowRemoveModal(false);
      dispatchSnackBarMessage(
        "driver-delete-success",
        "Entregador removido com sucesso",
        "success"
      );
    } catch (e) {
      console.log(e);
      dispatchSnackBarMessage(
        "driver-delete-error",
        "Erro ao remover entregador",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBlockDriversChange = async (event) => {
    try {
      const newCheckedValue = event.target.checked;

      const data = {
        config: {
          block_delivering_drivers: newCheckedValue,
        },
      };

      await setDeliveryCenterConfig(data);
      setChecked(newCheckedValue);
      dispatchSnackBarMessage(
        "config-update-success",
        `Entregadores ${
          newCheckedValue ? "bloqueados" : "desbloqueados"
        } com sucesso`,
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        "config-update-error",
        "Erro ao atualizar configuração",
        "error"
      );
    }
  };

  const handleSendMapSmsDriversChange = async (event) => {
    try {
      const newCheckedValue = event.target.checked;

      const data = {
        config: {
          send_driver_sms_maps_link: newCheckedValue,
        },
      };

      await setDeliveryCenterConfig(data);
      setCheckedSms(newCheckedValue);
      dispatchSnackBarMessage(
        "config-update-success",
        `Enviando SMS? ${newCheckedValue ? "Sim" : "Não"}`,
        "success"
      );
    } catch (e) {
      dispatchSnackBarMessage(
        "config-update-error",
        "Erro ao atualizar configuração",
        "error"
      );
    }
  };

  // Handle search reset
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Handle page refresh
  const handleRefresh = () => {
    loadDrivers(true, null);
  };

  // Directly render driver rows without animation variants
  const renderDriverRows = () => {
    return filteredDrivers.map((driver, index) => (
      <div key={`driver-list-${driver.driver_id}`} style={{ opacity: 1 }}>
        <DriverListRow
          index={index + 1}
          driver={driver}
          editAction={() => editDriver(driver.driver_id)}
          deleteAction={() => toggleDeleteModal(driver.driver_id)}
        />
      </div>
    ));
  };

  return (
    <>
      <AnimatedModal
        isOpen={show}
        onClose={closeModal}
        width="80%"
        maxWidth="1200px"
      >
        <ModalHeader
          title="Equipe de Entregadores"
          icon={DeliveryDiningIcon}
          onClose={closeModal}
        />

        <ModalContent>
          <Box sx={{ px: 3, pt: 2, pb: 4 }}>
            <AnimatePresence>
              {showError && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="error"
                    onClose={() => setShowError(false)}
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Blocking Drivers Status Card */}
            <ConfigSwitchCard
              icon={VerifiedUserIcon}
              title="Controle de Retorno"
              description={
                checked
                  ? "Entregadores não podem iniciar novas entregas enquanto não terem seu retorno confirmado"
                  : "Entregadores podem aceitar novas entregas normalmente"
              }
              isActive={checked}
              onChange={handleBlockDriversChange}
              activeLabel="Controle de Retorno Ativado"
              inactiveLabel="Entregadores Liberados"
              activeColor="warning"
              inactiveColor="success"
            />

            {/* SMS Drivers Dispatch */}
            <ConfigSwitchCard
              icon={SmsIcon}
              title="Enviar SMS ao Despachar"
              description={
                checkedSms
                  ? "SMS com link para mapas sendo enviado"
                  : "SMS com link para mapas não sendo enviado"
              }
              isActive={checkedSms}
              onChange={handleSendMapSmsDriversChange}
              activeLabel="Enviando SMS"
              inactiveLabel="Não enviando SMS"
            />

            {/* Actions Bar */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {/* Add Driver Button */}
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDriverModal}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    fontWeight: "medium",
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: theme.shadows[2],
                  }}
                >
                  Novo Entregador
                </Button>
              </motion.div>

              {/* Search Field */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                  maxWidth: { sm: 300 },
                  ml: { sm: "auto" },
                  mr: { sm: 2 },
                }}
              >
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 10,
                    color: theme.palette.text.secondary,
                    pointerEvents: "none",
                  }}
                />
                <Box
                  component="input"
                  placeholder="Buscar entregador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                    borderRadius: 2,
                    py: 1,
                    px: 4,
                    width: "100%",
                    outline: "none",
                    "&:focus": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(
                        theme.palette.primary.main,
                        0.2
                      )}`,
                    },
                    fontSize: "0.875rem",
                  }}
                />

                {searchTerm && (
                  <Tooltip title="Limpar busca">
                    <Box
                      component={motion.div}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      sx={{
                        position: "absolute",
                        right: 10,
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                      }}
                      onClick={handleClearSearch}
                    >
                      ×
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {/* Refresh Button */}
              <Tooltip title="Atualizar lista">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    disabled={loading}
                    sx={{
                      minWidth: 0,
                      p: 1,
                      borderRadius: 2,
                      aspectRatio: "1/1",
                    }}
                  >
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </Button>
                </motion.div>
              </Tooltip>
            </Box>

            {/* Drivers List */}
            {loading && isFirstLoad ? (
              <Stack spacing={2}>
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Stack>
            ) : (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  minHeight: 300,
                }}
              >
                <Box
                  ref={listRef}
                  sx={{
                    maxHeight: "calc(100vh - 400px)",
                    minHeight: 200,
                    overflow: "auto",
                    pr: 1,
                  }}
                >
                  {/* Empty state when no drivers match the search */}
                  {filteredDrivers.length === 0 && (
                    <Fade in={true}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 6,
                          px: 2,
                        }}
                      >
                        {searchTerm ? (
                          <>
                            <SearchIcon
                              sx={{
                                fontSize: 48,
                                color: alpha(theme.palette.text.secondary, 0.3),
                                mb: 2,
                              }}
                            />
                            <Typography
                              variant="h6"
                              color="textSecondary"
                              align="center"
                              gutterBottom
                            >
                              Nenhum resultado encontrado
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              align="center"
                              sx={{ mb: 3, maxWidth: 400 }}
                            >
                              Não encontramos entregadores correspondentes a "
                              {searchTerm}"
                            </Typography>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                variant="outlined"
                                onClick={handleClearSearch}
                                sx={{ borderRadius: 2 }}
                              >
                                Limpar Busca
                              </Button>
                            </motion.div>
                          </>
                        ) : (
                          <>
                            <DeliveryDiningIcon
                              sx={{
                                fontSize: 48,
                                color: alpha(theme.palette.text.secondary, 0.3),
                                mb: 2,
                              }}
                            />
                            <Typography
                              variant="h6"
                              color="textSecondary"
                              align="center"
                              gutterBottom
                            >
                              Nenhum entregador cadastrado
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              align="center"
                              sx={{ mb: 3, maxWidth: 400 }}
                            >
                              Cadastre seu primeiro entregador para começar a
                              gerenciar suas entregas
                            </Typography>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={openCreateDriverModal}
                                sx={{ borderRadius: 2 }}
                              >
                                Adicionar Entregador
                              </Button>
                            </motion.div>
                          </>
                        )}
                      </Box>
                    </Fade>
                  )}

                  {/* Driver list with direct rendering */}
                  {filteredDrivers.length > 0 && (
                    <Box sx={{ opacity: 1 }}>
                      {/* This key forces a complete re-render when data changes */}
                      <div
                        key={`driver-list-${forceUpdate}`}
                        style={{ opacity: 1 }}
                      >
                        {renderDriverRows()}

                        {/* Load more button */}
                        {drivers.length > 0 &&
                          showButtonMore &&
                          !searchTerm && (
                            <Box sx={{ mt: 2, opacity: 1 }}>
                              <Button
                                fullWidth
                                startIcon={
                                  loadingMore ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )
                                }
                                onClick={() => loadDrivers(false, null)}
                                disabled={loadingMore}
                                sx={{
                                  py: 1.2,
                                  borderRadius: 2,
                                  color: theme.palette.text.secondary,
                                  bgcolor: theme.palette.grey[50],
                                  border: `1px solid ${theme.palette.grey[200]}`,
                                  "&:hover": {
                                    bgcolor: theme.palette.grey[100],
                                    borderColor: theme.palette.grey[300],
                                  },
                                }}
                              >
                                {loadingMore ? "Carregando..." : "Mostrar mais"}
                              </Button>
                            </Box>
                          )}
                      </div>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Driver count badge */}
            {filteredDrivers.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Chip
                  label={`${filteredDrivers.length} ${
                    filteredDrivers.length === 1 ? "entregador" : "entregadores"
                  }`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </ModalContent>
      </AnimatedModal>

      {showCreateEditDriverModal && (
        <DriversCreateEdit
          id={driverToEditId}
          show={showCreateEditDriverModal}
          closeModal={closeCreateEditDriverModal}
          updateDrivers={() => loadDrivers(true)}
        />
      )}

      <RemoveModal
        title="Remover Entregador"
        toggleModal={toggleDeleteModal}
        onRemove={handleDeleteDriver}
        show={showRemoveModal}
        message="Tem certeza que deseja remover este entregador?"
      />
    </>
  );
}
